import * as Yup from 'yup';
import { useMemo } from 'react';
import { startOfDay } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import CardHeader from '@mui/material/CardHeader';
import LoadingButton from '@mui/lab/LoadingButton';
import LinearProgress from '@mui/material/LinearProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { useAppDispatcher } from 'src/redux/store';
import { AuthPermissions } from 'src/auth/permissions';
import { createLoanAgreement } from 'src/redux/slices/loan-agreement';

import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';

import { LOAN_APPROVERS, LoanBorrowerType } from 'src/types/loan-agreement';

import LoanBadDebtAlert from './loan-bad-debt-alert';
import { useBadDebtCheck } from './use-bad-debt-check';
import { toCents, earliestAllowedDate } from './utils';
import LoanBorrowerFields, {
  LOAN_BORROWER_DEFAULTS,
  loanBorrowerSchemaShape,
  LoanBorrowerFormValues,
} from './loan-borrower-fields';
import LoanPlanFields, {
  LOAN_PLAN_DEFAULTS,
  toLoanPlanPayload,
  loanPlanSchemaShape,
  LoanPlanFormValues,
} from './loan-plan-fields';

type FormValues = LoanBorrowerFormValues &
  LoanPlanFormValues & {
    capital: number;
    loanPaidDate: Date | null;
    approvedBy: string;
    acknowledgeBadDebt: boolean;
  };

export default function LoanAgreementNewForm() {
  const router = useRouter();

  const dispatch = useAppDispatcher();

  const { user: authUser } = useAuthContext();

  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const earliestPaidDate = earliestAllowedDate(isAdmin);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        ...loanBorrowerSchemaShape,
        ...loanPlanSchemaShape,
        capital: Yup.number().moreThan(0, 'Loan amount is required'),
        loanPaidDate: Yup.date()
          .nullable()
          .required('Loan paid date is required')
          .max(new Date(), 'Loan paid date cannot be in the future')
          .test(
            'recent',
            'Loan paid date must be today or within the last 2 days',
            (value) => !value || !earliestPaidDate || startOfDay(value) >= earliestPaidDate
          ),
        approvedBy: Yup.string().oneOf(LOAN_APPROVERS).required('Select who approved the loan'),
        acknowledgeBadDebt: Yup.boolean(),
      }),
    [earliestPaidDate]
  );

  const methods = useForm<FormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema as any, { context: { planRequired: true } }),
    defaultValues: {
      ...LOAN_BORROWER_DEFAULTS,
      ...LOAN_PLAN_DEFAULTS,
      capital: 0,
      loanPaidDate: startOfDay(new Date()),
      approvedBy: '',
      acknowledgeBadDebt: false,
    },
  });

  const {
    watch,
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const { matches, checking } = useBadDebtCheck(values);

  const blocked = matches.length > 0 && !isAdmin;
  const needsAcknowledgement = matches.length > 0 && isAdmin && !values.acknowledgeBadDebt;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const isEmployee = data.borrowerType === LoanBorrowerType.EMPLOYEE;
      const created = await dispatch(
        createLoanAgreement({
          agreement: {
            borrowerType: data.borrowerType,
            employee: isEmployee ? (data.employee as string) : undefined,
            borrowerName: isEmployee ? undefined : data.borrowerName.trim(),
            nicNumber: isEmployee ? undefined : data.nicNumber.trim() || undefined,
            phoneNumber: isEmployee ? undefined : data.phoneNumber.trim() || undefined,
            capitalInCents: toCents(data.capital),
            loanPaidDate: (data.loanPaidDate as Date).toISOString(),
            approvedBy: data.approvedBy,
            acknowledgeBadDebt: matches.length > 0 ? data.acknowledgeBadDebt : undefined,
            ...toLoanPlanPayload(data),
          },
        })
      ).unwrap();

      enqueueSnackbar('Loan agreement created');
      router.push(paths.dashboard.loans.view(created._id));
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message, { variant: 'error' });
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3}>
        <Card>
          <CardHeader title="Borrower" />
          {checking && <LinearProgress sx={{ mx: 3, mt: 2, height: 2 }} />}
          <Stack spacing={2} sx={{ p: 3 }}>
            <LoanBorrowerFields />
            <LoanBadDebtAlert matches={matches} isAdmin={isAdmin} />
          </Stack>
        </Card>

        <Card
          aria-disabled={blocked}
          sx={blocked ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
        >
          <CardHeader title="Loan" />
          <Grid container spacing={2} sx={{ p: 3 }}>
            <Grid xs={12} md={4}>
              <RHFTextField name="capital" label="Loan Amount (Rupees)" type="number" required />
            </Grid>

            <Grid xs={12} md={4}>
              <Controller
                name="loanPaidDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="Loan Paid Date"
                    value={field.value}
                    onChange={field.onChange}
                    disableFuture
                    minDate={earliestPaidDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!error,
                        helperText:
                          error?.message ?? (isAdmin ? undefined : 'Today or up to 2 days back'),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <RHFSelect name="approvedBy" label="Approved By" required>
                {LOAN_APPROVERS.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>
          </Grid>
        </Card>

        <Card
          aria-disabled={blocked}
          sx={blocked ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
        >
          <CardHeader title="Agreement" subheader="How the loan will be repaid" />
          <Stack sx={{ p: 3 }}>
            <LoanPlanFields
              capitalInCents={toCents(values.capital)}
              earliestDueDate={values.loanPaidDate}
            />
          </Stack>
        </Card>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button component={RouterLink} href={paths.dashboard.loans.root()} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={blocked || needsAcknowledgement || checking}
          >
            Create Agreement
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
